import React, { useMemo } from 'react';

// Styles & Assets
import './styles/Globals.css';
import './styles/Context.css';

// General Pages
import ErrorPage from './pages/Error/ErrorPage';
import LoginPage from './pages/Login/LoginPage';
import ValidateLoginPage from './pages/ValidateLogin/ValidateLoginPage';
import SideNavbarWrapper from './pages/SideNavbarWrapper/SideNavbarWrapper';
import UbikarePage from './pages/Ubikare/UbikarePage';

//Patients Pages
import PatientsPage from './pages/Patients/PatientsPage';
import PatientPage from './pages/Patient/PatientPage';
import PatientChartPage from './pages/PatientChart/PatientChartPage';
import PatientTables from './pages/PatientTables/PatientTables';
import PatientPollsPage from './pages/PatientPolls/PatientPollsPage';
import PatientPharmacyPage from './pages/PatientPharmacy/PatientPharmacyPage';
import ClinicalCoursePage from './pages/ClinicalCourse/ClinicalCoursePage';
import PatientCalendarPage from './pages/PatientCalendarPage/PatientCalendarPage';
import AdditionalDataPage from './pages/AdditionalData/AdditionalDataPage';

// CalendarPage
import CalendarPage from './pages/Calendar/CalendarPage';

// Poll Pages
import CreateEditPollPage from './pages/CreateEditPoll/CreateEditPollPage';
import PollsPage from './pages/Polls/PollsPage';

// Route Pages
import VisitRoutesPage from './pages/VisitRoutes/VisitRoutesPage';
import SetupRoutePage from './pages/SetupRoute/SetupRoutePage';
import GenerateRoutePage from './pages/GenerateRoute/GenerateRoutePage';

// Administration Pages
import SettingsAdministrationPage from './pages/SettingsAdministration/SettingsAdministrationPage';
import UserAdministrationPage from './pages/UserAdministration/UserAdministrationPage';
import DevicesManagementPage from './pages/DevicesManagement/DevicesManagementPage';
import ZonesBedManagementPage from './pages/ZonesBedManagement/ZonesBedManagementPage';
import ScoresManagementPage from './pages/ScoresManagement/ScoresManagementPage';
import ScorePage from './pages/ScorePage/ScorePage';
import ScoreAddPage from './pages/ScoreAdd/ScoreAddPage';
import PreconfigurationPage from './pages/PreconfigurationPage/PreconfigurationPage';
import CreatePreconfigurationPage from './pages/CreatePreconfigurationPage/CreatePreconfigurationPage';

// Loaders
import { checkAuthenticatedLoader, LogoutAction, ubikareAuthenticatedLoader } from './services/Auth';
import { accessAdministrationLoader, accessUpdatePollRoutesLoader } from './services/routes';

// Other
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import ErrorSpinnerWrapper from './components/UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import ReportsPage from './pages/ReportsPage/ReportsPage';
import PatientCasesPage from './pages/PatientCasesPage/PatientCasesPage';
import { checkAdministrationRouteEnabled, checkPatientButtonsEnabled, checkRouteEnabled } from './services/routes';

// App Components: Renders the router provider and the Patients and Polls Context as a wrapper of all the app.
const App: React.FC = () => {

    const Routes = useMemo(() => createBrowserRouter([
        {
            path: '/',
            errorElement: <ErrorPage />,
            element: <Navigate to="/home/patients" />,
        },
        {
            path: 'login', element: <LoginPage />,
        },
        {
            path: 'logout', loader: LogoutAction, errorElement: <ErrorSpinnerWrapper loading={false} error={'Error cerrando sesión'} />,
        },
        {
            path: 'validatelogin',
            element: <ValidateLoginPage />,
        },
        {
            path: '/home',
            element: <SideNavbarWrapper />,
            id: 'root',
            loader: checkAuthenticatedLoader,
            children: [
                {
                    path: 'patients',
                    children: [
                        {
                            path: '',
                            element: <PatientsPage />,
                        },
                        {
                            path: 'cases',
                            element: <PatientCasesPage />
                        },
                        {
                            path: 'cases/:id',
                            element: <PatientPage />,
                            children: [
                                {
                                    path: '',
                                    element: <Navigate to='patients_charts' />,
                                },
                                {
                                    path: 'patients_charts',
                                    element: <PatientChartPage />,
                                },
                                {
                                    path: 'patients_data_table',
                                    element: <PatientTables />
                                },
                                {
                                    path: 'patients_polls',
                                    element: <PatientPollsPage />
                                },
                                {
                                    path: 'pharmacy',
                                    loader: checkPatientButtonsEnabled('pharmacy'),
                                    element: <PatientPharmacyPage />
                                },
                                {
                                    path: 'reports',
                                    element: <ReportsPage />
                                },
                                {
                                    path: 'clinical_course',
                                    loader: checkPatientButtonsEnabled('clinical_course'),
                                    element: <ClinicalCoursePage />
                                },
                                {
                                    path: 'calendar',
                                    element: <PatientCalendarPage />
                                },
                                {
                                    path: 'additional_data',
                                    element: <AdditionalDataPage />
                                }
                            ]
                        },
                    ],
                },
                {
                    path: 'calendar',
                    loader: checkRouteEnabled('calendar'),
                    children: [
                        {
                            path: '',
                            element: <CalendarPage />
                        }
                    ]
                },
                {
                    path: 'polls',
                    loader: checkRouteEnabled('polls'),
                    children: [
                        {

                            path: '',
                            element: <PollsPage />,

                        },
                        {
                            path: 'create_poll',
                            loader: accessUpdatePollRoutesLoader,
                            element: <CreateEditPollPage />
                        },
                        {
                            path: 'edit_poll/:id',
                            loader: accessUpdatePollRoutesLoader,
                            element: <CreateEditPollPage />
                        },
                    ]
                },
                {
                    path: 'routes',
                    loader: checkRouteEnabled('routes'),
                    children: [
                        {
                            path: 'routes_map_patients',
                            element: <VisitRoutesPage />,
                        },
                        {
                            path: 'generate_new_route',
                            element: <GenerateRoutePage />,
                        },
                        {
                            path: 'set-up-route',
                            element: <SetupRoutePage />,
                        },
                    ]
                },
                {
                    path: 'administration',
                    loader: checkRouteEnabled('administration') && accessAdministrationLoader,
                    children: [
                        {
                            path: '',
                            element: <SettingsAdministrationPage />,
                        },
                        {
                            path: 'user_management',
                            loader: checkAdministrationRouteEnabled('user_management'),
                            element: <UserAdministrationPage />,
                        },
                        {
                            path: 'devices_management',
                            loader: checkAdministrationRouteEnabled('devices_management'),
                            element: <DevicesManagementPage />
                        },
                        {
                            path: 'zones_bed_management',
                            loader: checkAdministrationRouteEnabled('zones_bed_management'),
                            element: <ZonesBedManagementPage />
                        },
                        {
                            path: 'scores_management',
                            loader: checkAdministrationRouteEnabled('scores_management'),
                            element: <ScoresManagementPage />,
                        },
                        {
                            path: 'scores_management/:id',
                            element: <ScorePage />
                        },
                        {
                            path: 'scores_management/add-score',
                            element: <ScoreAddPage />
                        },
                        {
                            path: 'preconfiguration',
                            loader: checkAdministrationRouteEnabled('preconfiguration'),
                            element: <PreconfigurationPage />
                        },
                        {
                            path: 'preconfiguration/create-preconfig',
                            element: <CreatePreconfigurationPage />
                        }
                    ]
                },

            ]
        }, {
            path: 'ubikare',
            loader: ubikareAuthenticatedLoader,
            children: [
                {
                    path: '',
                    element: <UbikarePage />
                }
            ]
        },
    ]), []);

    const colors = window.config.APP_GENERIC_COLORS
    document.documentElement.style.setProperty('--primary-color', colors.primaryColor);
    document.documentElement.style.setProperty('--primary-color-light', colors.primaryColorLight);
    document.documentElement.style.setProperty('--primary-color-lighter', colors.primaryColorLighter);
    document.documentElement.style.setProperty('--secondary-color', colors.secondaryColor);
    document.documentElement.style.setProperty('--secondary-color-light', colors.secondaryColorLight);
    document.documentElement.style.setProperty('--secondary-color-lighter', colors.secondaryColorLighter);
    document.documentElement.style.setProperty('--discharge-status-had', colors.dischargeStatusHad);
    document.documentElement.style.setProperty('--discharge-status-seven-days', colors.dischargeStatusSevendays);
    document.documentElement.style.setProperty('--discharge-status-thirty-days', colors.dischargeStatusThirtydays);
    document.documentElement.style.setProperty('--highlighted-row', colors.highlightedRow);
    document.documentElement.style.setProperty('--sidenav-gradient', colors.sidenavGradient);

    return <RouterProvider router={Routes} />;
};

export default App;
